import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
  Loader2,
  Workflow,
  ExternalLink,
  QrCode,
  Smartphone,
  MessageSquare,
  Clock,
  TimerReset,
  Bot,
  Users,
  Plug,
} from "lucide-react";
import { Link as RouterLink } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import BusinessHoursEditor from "../BusinessHoursEditor";
import Secao from "./Secao";

// Os resumos abaixo respondem, sem abrir nada, à pergunta "isso aqui está
// ligado?". É o que faz a tela de edição caber numa olhada.

const DIAS_CURTOS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

const resumoMensagens = (v) => {
  const ativas = [
    v.greetingMessage && "saudação",
    v.farewellMessage && "despedida",
    v.ratingMessage && "pesquisa de satisfação",
  ].filter(Boolean);

  return ativas.length
    ? `Enviando ${ativas.join(", ")}`
    : "Nenhuma mensagem automática";
};

const resumoHorarios = (v) => {
  const abertos = (Array.isArray(v.businessHours) ? v.businessHours : [])
    .filter((h) => h.aberto)
    .sort((a, b) => Number(a.dia) - Number(b.dia));

  if (!abertos.length) return "Atende 24 horas, todos os dias";

  const faixa = `${abertos[0].inicio || "00:00"} às ${abertos[0].fim || "23:59"}`;
  const dias = abertos.map((h) => DIAS_CURTOS[Number(h.dia)]).join(", ");

  return `${dias} · ${faixa}`;
};

const resumoParado = (v) => {
  const regras = [
    Number(v.expiresTicket) > 0 && `Encerra após ${v.expiresTicket}h`,
    Number(v.timeSendQueue) > 0 && `move de fila em ${v.timeSendQueue}min`,
    Number(v.timeInactiveMessage) > 0 &&
      `pergunta em ${v.timeInactiveMessage}min`,
  ].filter(Boolean);

  return regras.length ? regras.join(" · ") : "Nada acontece sozinho";
};

const resumoRobo = (v, fluxoId) => {
  const ligados = [
    v.aiEnabled && "IA respondendo",
    fluxoId && fluxoId !== NO_FLOW && "fluxo ativo",
  ].filter(Boolean);

  return ligados.length ? ligados.join(" · ") : "Desligado";
};

const resumoGrupos = (v) => {
  const partes = [
    v.groupAsTicket ? "Grupos viram atendimento" : "Grupos ignorados",
    v.slaCountGroups ? "grupos contam no SLA" : "grupos fora do SLA",
    v.rejectCalls ? "chamadas recusadas" : "chamadas aceitas",
    v.readReceipts === false ? "visto azul desativado" : "visto azul ativo",
  ];

  return partes.join(" · ");
};

const resumoIntegracoes = (v) => {
  const partes = [
    v.token && "token da API",
    v.wavoipToken && "Wavoip",
  ].filter(Boolean);

  return partes.length ? `Configurado: ${partes.join(", ")}` : "Nada configurado";
};

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Muito curto")
    .max(50, "Muito longo")
    .required("Campo obrigatório"),
});

const NO_FLOW = "none";
const NO_AGENT = "none";
// O Select do Radix não aceita value="" — precisa de um valor sentinela.
const SEM_FILA = "none";

const WhatsAppModal = ({ open, onClose, whatsAppId, onCreated }) => {
  // Criar e configurar são tarefas diferentes. Quem está criando quer uma
  // conexão no ar: nome e QR Code, nada mais. As 25 opções das abas são para
  // depois, com a conexão já funcionando — e é isso que a tela de edição faz.
  const modoCriacao = !whatsAppId;

  const initialState = {
    name: "",
    greetingMessage: "",
    farewellMessage: "",
    rejectCalls: false,
    wavoipToken: "",
    expiresTicket: 0,
    expiresInactiveMessage: "",
    timeCreateNewTicket: 0,
    reopenSameTicket: true,
    timeSendQueue: 0,
    sendIdQueue: null,
    ratingMessage: "",
    confirmCloseEnabled: false,
    confirmCloseMessage: "",
    resolvedMessage: "",
    confirmCloseReminderMinutes: 0,
    confirmCloseReminderMessage: "",
    timeInactiveMessage: 0,
    inactiveMessage: "",
    maxUseBotQueues: 0,
    businessHours: [],
    outOfHoursMessage: "",
    collectiveVacationStart: "",
    collectiveVacationEnd: "",
    collectiveVacationMessage: "",
    token: "",
    whenExpiresTicket: "qualquer",
    aiEnabled: false,
    aiPrompt: "",
    aiMaxMessages: 5,
    groupAsTicket: true,
    slaCountGroups: false,
    importHistory: false,
    importTicketStatus: "closed",
    timeUseBotQueues: 0,
    showGroups: false,
    callRejectMessage: "",
    isDefault: false,
    readReceipts: true,
  };
  const [whatsApp, setWhatsApp] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [flows, setFlows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filas, setFilas] = useState([]);
  const [selectedFlowId, setSelectedFlowId] = useState(NO_FLOW);
  const [selectedAgentId, setSelectedAgentId] = useState(NO_AGENT);
  // Uma seção aberta por vez: com todas abertas o modal volta a ser a parede
  // de campos que era.
  const [secaoAberta, setSecaoAberta] = useState(null);

  const alternarSecao = (id) =>
    setSecaoAberta((atual) => (atual === id ? null : id));

  useEffect(() => {
    // Na criação nada disso aparece na tela; buscar seria gasto à toa.
    if (!open || modoCriacao) return;
    (async () => {
      try {
        const [f, q, a] = await Promise.all([
          api.get("/flows"),
          api.get("/queue"),
          api.get("/ai/agents"),
        ]);
        setFlows(Array.isArray(f.data) ? f.data : []);
        setFilas(Array.isArray(q.data) ? q.data : []);
        setAgents(Array.isArray(a.data) ? a.data : []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open, modoCriacao]);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`whatsapp/${whatsAppId}`);
        setWhatsApp(data);

        const whatsQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(whatsQueueIds);
        setSelectedFlowId(data.flowId ? String(data.flowId) : NO_FLOW);
        setSelectedAgentId(
          data.aiAgentId ? String(data.aiAgentId) : NO_AGENT
        );
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  const handleSaveWhatsApp = async (values) => {
    const whatsappData = {
      ...values,
      queueIds: selectedQueueIds,
      flowId: selectedFlowId === NO_FLOW ? null : +selectedFlowId,
      aiAgentId: selectedAgentId === NO_AGENT ? null : +selectedAgentId,
    };

    try {
      if (whatsAppId) {
        await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
        toast.success(i18n.t("whatsappModal.success"));
        handleClose();
        return;
      }

      const { data: criada } = await api.post("/whatsapp", whatsappData);
      handleClose();
      // O backend já iniciou a sessão: o QR Code está a caminho. Levar a pessoa
      // direto para ele evita o passo em que ela salva, olha a lista e não sabe
      // que ainda falta parear o celular.
      if (onCreated) onCreated(criada);
    } catch (err) {
      toastError(err);
    }
  };

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
    setSelectedFlowId(NO_FLOW);
    setSelectedAgentId(NO_AGENT);
    // Sem isto, reabrir o modal traz a última seção aberta — o que faz parecer
    // que ficou algo pendente ali.
    setSecaoAberta(null);
  };

  const activeFlows = flows.filter((flow) => flow.isActive);
  const activeAgents = agents.filter((agent) => agent.isActive);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {modoCriacao
              ? "Conectar um WhatsApp"
              : i18n.t("whatsappModal.title.edit")}
          </DialogTitle>
          {modoCriacao && (
            <p className="text-sm text-muted-foreground">
              São dois passos: dar um nome e ler o QR Code com o celular.
            </p>
          )}
        </DialogHeader>
        <Formik
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={SessionSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveWhatsApp(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting, setFieldValue }) => (
            <Form className="space-y-4">
              {modoCriacao ? (
                <div className="space-y-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="name">Nome desta conexão</Label>
                    <Field
                      as={Input}
                      id="name"
                      name="name"
                      autoFocus
                      placeholder="Ex.: Vendas, Suporte, Loja Centro"
                    />
                    {touched.name && errors.name ? (
                      <p className="text-xs text-destructive">{errors.name}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Só para você reconhecer o número aqui dentro. Pode
                        trocar depois.
                      </p>
                    )}
                  </div>

                  <div className="rounded-lg border bg-muted/40 p-4">
                    <p className="mb-3 flex items-center gap-2 text-sm font-medium">
                      <Smartphone className="h-4 w-4" />
                      Como vai funcionar
                    </p>
                    <ol className="space-y-2.5 text-sm text-muted-foreground">
                      <li className="flex gap-2.5">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-background text-xs font-medium text-foreground">
                          1
                        </span>
                        Ao salvar, aparece um QR Code na tela.
                      </li>
                      <li className="flex gap-2.5">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-background text-xs font-medium text-foreground">
                          2
                        </span>
                        No celular, abra o WhatsApp e vá em{" "}
                        <strong className="font-medium text-foreground">
                          Aparelhos conectados
                        </strong>
                        .
                      </li>
                      <li className="flex gap-2.5">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-background text-xs font-medium text-foreground">
                          3
                        </span>
                        Aponte a câmera para o QR Code. Pronto — as mensagens
                        começam a chegar aqui.
                      </li>
                    </ol>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Mensagem de saudação, horário de atendimento, chatbot e filas
                    ficam no botão de editar da conexão, depois que ela estiver
                    conectada.
                  </p>
                </div>
              ) : (
              <div className="space-y-3">
                {/* O que quase todo mundo vem mexer fica à vista. */}
                  <div className="space-y-1.5">
                    <Label htmlFor="name">
                      {i18n.t("whatsappModal.form.name")}
                    </Label>
                    <Field as={Input} id="name" name="name" autoFocus />
                    {touched.name && errors.name && (
                      <p className="text-xs text-destructive">{errors.name}</p>
                    )}
                  </div>


                  <div>
                    <QueueSelect
                      selectedQueueIds={selectedQueueIds}
                      onChange={(selectedIds) =>
                        setSelectedQueueIds(selectedIds)
                      }
                    />
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      Com uma fila, os tickets entram nela automaticamente. Com
                      várias, o cliente escolhe por um menu numérico.
                    </p>
                  </div>


                  <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                    <div>
                      <Label
                        htmlFor="isDefault"
                        className="text-sm font-medium"
                      >
                        {i18n.t("whatsappModal.form.default")}
                      </Label>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Conexão usada quando nenhuma outra for especificada.
                      </p>
                    </div>
                    <Switch
                      id="isDefault"
                      checked={values.isDefault}
                      onCheckedChange={(checked) =>
                        setFieldValue("isDefault", checked)
                      }
                    />
                  </div>


                <p className="pt-1 text-xs text-muted-foreground">
                  O resto é opcional. Cada linha abaixo mostra como está hoje —
                  abra só a que for mexer.
                </p>

                <Secao
                  titulo="Mensagens automáticas"
                  resumo={resumoMensagens(values)}
                  icone={MessageSquare}
                  aberta={secaoAberta === "mensagens"}
                  onToggle={() => alternarSecao("mensagens")}
                >
                  <div className="space-y-1.5">
                    <Label htmlFor="greetingMessage">
                      {i18n.t("queueModal.form.greetingMessage")}
                    </Label>
                    <Field
                      as={Textarea}
                      id="greetingMessage"
                      name="greetingMessage"
                      rows={4}
                      placeholder="Olá! Como podemos ajudar?"
                    />
                    <p className="text-xs text-muted-foreground">
                      Enviada ao abrir um novo atendimento. Com mais de uma
                      fila, é usada junto do menu de escolha.
                    </p>
                  </div>


                  <div className="space-y-1.5">
                    <Label htmlFor="farewellMessage">
                      {i18n.t("whatsappModal.form.farewellMessage")}
                    </Label>
                    <Field
                      as={Textarea}
                      id="farewellMessage"
                      name="farewellMessage"
                      rows={4}
                      placeholder="Atendimento encerrado. Obrigado pelo contato!"
                    />
                    <p className="text-xs text-muted-foreground">
                      Enviada quando o ticket é finalizado. Use{" "}
                      <code className="rounded bg-muted px-1">
                        {"{{nome}}"}
                      </code>{" "}
                      para o nome do cliente e{" "}
                      <code className="rounded bg-muted px-1">
                        {"{{protocolo}}"}
                      </code>{" "}
                      para o número do atendimento.
                    </p>
                  </div>


                  <div className="space-y-1.5 rounded-lg border p-3">
                    <Label htmlFor="ratingMessage">
                      Pesquisa de satisfação
                    </Label>
                    <Field
                      as={Textarea}
                      id="ratingMessage"
                      name="ratingMessage"
                      rows={2}
                      placeholder="De 0 a 10, o quanto você recomendaria nosso atendimento?"
                    />
                    <p className="text-xs text-muted-foreground">
                      Enviada ao encerrar. A próxima resposta com um número de 0
                      a 10 vira a nota. Em branco, não pergunta.
                    </p>
                  </div>
                  {/* Quem sabe se o problema foi resolvido é o cliente. Aqui a
                      equipe propõe o encerramento e ele confirma no botão. */}
                  <div className="space-y-3 rounded-lg border p-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-0.5">
                        <Label htmlFor="confirmCloseEnabled" className="text-sm">
                          Cliente confirma o encerramento
                        </Label>
                        <p className="text-xs leading-relaxed text-muted-foreground">
                          Ao encerrar, em vez de fechar na hora, o cliente
                          recebe um botão perguntando se pode encerrar. O
                          atendimento continua aberto até ele responder.
                        </p>
                      </div>
                      <Switch
                        id="confirmCloseEnabled"
                        checked={Boolean(values.confirmCloseEnabled)}
                        onCheckedChange={(v) =>
                          setFieldValue("confirmCloseEnabled", v)
                        }
                      />
                    </div>

                    {values.confirmCloseEnabled && (
                      <div className="space-y-3 border-t pt-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="confirmCloseMessage">
                            Pergunta enviada
                          </Label>
                          <Field
                            as={Textarea}
                            id="confirmCloseMessage"
                            name="confirmCloseMessage"
                            rows={2}
                            placeholder="Posso encerrar o seu atendimento? Se estiver tudo resolvido, toque em Finalizar atendimento."
                          />
                          <p className="text-xs text-muted-foreground">
                            Vai junto com os botões “Finalizar atendimento” e
                            “Ainda preciso de ajuda”. Em branco, usa um texto
                            padrão.
                          </p>
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="resolvedMessage">
                            Mensagem de resolvido
                          </Label>
                          <Field
                            as={Textarea}
                            id="resolvedMessage"
                            name="resolvedMessage"
                            rows={2}
                            placeholder="Atendimento encerrado como resolvido. Se precisar, é só mandar mensagem."
                          />
                          <p className="text-xs text-muted-foreground">
                            Enviada depois que o cliente confirma, antes da
                            pesquisa de satisfação.
                          </p>
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="confirmCloseReminderMinutes">
                            Lembrar depois de (minutos)
                          </Label>
                          <Field
                            as={Input}
                            id="confirmCloseReminderMinutes"
                            name="confirmCloseReminderMinutes"
                            type="number"
                            min={0}
                          />
                          <p className="text-xs text-muted-foreground">
                            Quem não responder recebe um lembrete, uma única
                            vez. Passado o mesmo prazo de novo em silêncio, o
                            atendimento encerra sozinho como “sem retorno”. Zero
                            não lembra nem encerra — o atendimento fica aberto
                            aguardando.
                          </p>
                        </div>

                        {Number(values.confirmCloseReminderMinutes) > 0 && (
                          <div className="space-y-1.5">
                            <Label htmlFor="confirmCloseReminderMessage">
                              Texto do lembrete
                            </Label>
                            <Field
                              as={Textarea}
                              id="confirmCloseReminderMessage"
                              name="confirmCloseReminderMessage"
                              rows={2}
                              placeholder="Só confirmando: o seu atendimento foi concluído?"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Secao>

                <Secao
                  titulo="Horário de atendimento"
                  resumo={resumoHorarios(values)}
                  icone={Clock}
                  aberta={secaoAberta === "horarios"}
                  onToggle={() => alternarSecao("horarios")}
                >
                  <BusinessHoursEditor
                    valor={values.businessHours}
                    onChange={(v) => setFieldValue("businessHours", v)}
                  />


                  <div className="space-y-1.5">
                    <Label htmlFor="outOfHoursMessage">
                      Mensagem fora do horário
                    </Label>
                    <Field
                      as={Textarea}
                      id="outOfHoursMessage"
                      name="outOfHoursMessage"
                      rows={2}
                      placeholder="Recebemos sua mensagem! Nosso atendimento é de segunda a sexta, das 9h às 18h."
                    />
                  </div>


                  <div className="space-y-3 rounded-lg border p-3">
                    <p className="text-sm font-medium">Férias coletivas</p>
                    <p className="-mt-2 text-xs text-muted-foreground">
                      No período, esta mensagem substitui a de fora do horário.
                    </p>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="collectiveVacationStart">De</Label>
                        <Field
                          as={Input}
                          id="collectiveVacationStart"
                          name="collectiveVacationStart"
                          type="date"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="collectiveVacationEnd">Até</Label>
                        <Field
                          as={Input}
                          id="collectiveVacationEnd"
                          name="collectiveVacationEnd"
                          type="date"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="collectiveVacationMessage">
                        Mensagem
                      </Label>
                      <Field
                        as={Textarea}
                        id="collectiveVacationMessage"
                        name="collectiveVacationMessage"
                        rows={2}
                        placeholder="Estamos em férias coletivas e voltamos dia 05/01. Sua mensagem será respondida no retorno."
                      />
                    </div>
                  </div>
                </Secao>

                <Secao
                  titulo="Atendimento parado"
                  resumo={resumoParado(values)}
                  icone={TimerReset}
                  aberta={secaoAberta === "parado"}
                  onToggle={() => alternarSecao("parado")}
                >
                  <p className="text-xs text-muted-foreground">
                    O servidor confere de 5 em 5 minutos. Tudo em 0 = nada
                    acontece sozinho.
                  </p>

                  {/* Regras que o servidor aplica sozinho, de 5 em 5 minutos. */}
                  <div className="space-y-3 rounded-lg border p-3">
                    <p className="text-sm font-medium">
                      Encerramento automático
                    </p>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="expiresTicket">
                          Encerrar após (horas)
                        </Label>
                        <Field
                          as={Input}
                          id="expiresTicket"
                          name="expiresTicket"
                          type="number"
                          min="0"
                        />
                        <p className="text-xs text-muted-foreground">
                          0 = nunca encerra sozinho.
                        </p>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="timeCreateNewTicket">
                          Reabrir até (minutos)
                        </Label>
                        <Field
                          as={Input}
                          id="timeCreateNewTicket"
                          name="timeCreateNewTicket"
                          type="number"
                          min="0"
                        />
                        <p className="text-xs text-muted-foreground">
                          Depois disso, uma mensagem abre um atendimento novo. 0
                          = 2 horas. Só vale com a opção abaixo desligada.
                        </p>
                      </div>
                    </div>

                    {/* Um atendimento por contato. Desligado, o cliente que
                        volta a escrever depois do prazo acima ganha um
                        atendimento novo — e o histórico dele fica picado em
                        vários chamados. */}
                    <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <Label htmlFor="reopenSameTicket">
                          Reabrir o mesmo atendimento
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          O cliente que volta a escrever reabre o atendimento
                          encerrado, em vez de abrir outro. Desligue apenas se
                          a operação conta chamado por chamado.
                        </p>
                      </div>
                      <Switch
                        id="reopenSameTicket"
                        checked={values.reopenSameTicket !== false}
                        onCheckedChange={(v) =>
                          setFieldValue("reopenSameTicket", v)
                        }
                      />
                    </div>

                    {Number(values.expiresTicket) > 0 && (
                      <div className="space-y-1.5">
                        <Label>Contar a inatividade a partir de</Label>
                        <Select
                          value={values.whenExpiresTicket || "qualquer"}
                          onValueChange={(v) =>
                            setFieldValue("whenExpiresTicket", v)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="qualquer">
                              Qualquer mensagem
                            </SelectItem>
                            <SelectItem value="cliente">
                              Só quando o cliente parou de responder
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Com a segunda opção, um atendimento em que a equipe
                          ainda deve resposta nunca é encerrado sozinho.
                        </p>
                      </div>
                    )}

                    {Number(values.expiresTicket) > 0 && (
                      <div className="space-y-1.5">
                        <Label htmlFor="expiresInactiveMessage">
                          Aviso antes de encerrar
                        </Label>
                        <Field
                          as={Textarea}
                          id="expiresInactiveMessage"
                          name="expiresInactiveMessage"
                          rows={2}
                          placeholder="Encerramos este atendimento por inatividade. É só escrever de novo que retomamos."
                        />
                        <p className="text-xs text-muted-foreground">
                          Em branco, encerra sem avisar.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 rounded-lg border p-3">
                    <p className="text-sm font-medium">Fila de socorro</p>
                    <p className="-mt-2 text-xs text-muted-foreground">
                      Se ninguém assumir a tempo, o atendimento cai numa fila em
                      vez de ficar esquecido.
                    </p>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="timeSendQueue">Após (minutos)</Label>
                        <Field
                          as={Input}
                          id="timeSendQueue"
                          name="timeSendQueue"
                          type="number"
                          min="0"
                        />
                        <p className="text-xs text-muted-foreground">
                          0 = desligado.
                        </p>
                      </div>

                      {Number(values.timeSendQueue) > 0 && (
                        <div className="space-y-1.5">
                          <Label>Mover para a fila</Label>
                          <Select
                            value={
                              values.sendIdQueue
                                ? String(values.sendIdQueue)
                                : SEM_FILA
                            }
                            onValueChange={(v) =>
                              setFieldValue(
                                "sendIdQueue",
                                v === SEM_FILA ? null : Number(v),
                              )
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Escolha a fila" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={SEM_FILA}>Nenhuma</SelectItem>
                              {filas.map((f) => (
                                <SelectItem key={f.id} value={String(f.id)}>
                                  {f.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3 rounded-lg border p-3">
                    <p className="text-sm font-medium">Aviso de inatividade</p>
                    <p className="-mt-2 text-xs text-muted-foreground">
                      Pergunta se a pessoa ainda está por ali antes de encerrar.
                      Sai uma vez só por atendimento.
                    </p>

                    <div className="space-y-1.5">
                      <Label htmlFor="timeInactiveMessage">
                        Após (minutos)
                      </Label>
                      <Field
                        as={Input}
                        id="timeInactiveMessage"
                        name="timeInactiveMessage"
                        type="number"
                        min="0"
                      />
                      <p className="text-xs text-muted-foreground">
                        0 = desligado.
                      </p>
                    </div>

                    {Number(values.timeInactiveMessage) > 0 && (
                      <div className="space-y-1.5">
                        <Label htmlFor="inactiveMessage">Mensagem</Label>
                        <Field
                          as={Textarea}
                          id="inactiveMessage"
                          name="inactiveMessage"
                          rows={2}
                          placeholder="Você ainda está aí? Se precisar de mais alguma coisa, é só escrever."
                        />
                      </div>
                    )}
                  </div>
                </Secao>

                <Secao
                  titulo="Robô e inteligência artificial"
                  resumo={resumoRobo(values, selectedFlowId)}
                  icone={Bot}
                  aberta={secaoAberta === "robo"}
                  onToggle={() => alternarSecao("robo")}
                >
                  <div className="space-y-3 rounded-lg border p-3">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <Label
                          htmlFor="aiEnabled"
                          className="text-sm font-medium"
                        >
                          Responder com IA
                        </Label>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Enquanto ninguém assumiu o atendimento. Com um
                          atendente na conversa, quem responde é ele.
                        </p>
                      </div>
                      <Switch
                        id="aiEnabled"
                        checked={values.aiEnabled}
                        onCheckedChange={(v) => setFieldValue("aiEnabled", v)}
                      />
                    </div>

                    {values.aiEnabled && (
                      <div className="space-y-1.5">
                        <Label>Agente</Label>
                        <Select
                          value={selectedAgentId}
                          onValueChange={setSelectedAgentId}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Nenhum agente" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NO_AGENT}>
                              Nenhum agente
                            </SelectItem>
                            {activeAgents.map((agent) => (
                              <SelectItem key={agent.id} value={String(agent.id)}>
                                {agent.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          A instrução, o provedor e o limite de respostas vêm do
                          agente. Sem agente escolhido, a IA não responde.
                        </p>

                        {activeAgents.length === 0 && (
                          <div className="rounded-lg border border-dashed p-4 text-center">
                            <p className="text-sm text-muted-foreground">
                              Nenhum agente criado ainda.
                            </p>
                            <RouterLink to="/ai" onClick={handleClose}>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="mt-2"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                                Criar um agente
                              </Button>
                            </RouterLink>
                          </div>
                        )}
                      </div>
                    )}
                  </div>



                  <div className="space-y-3 rounded-lg border p-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="timeUseBotQueues">
                        Chatbot desiste após (minutos sem escolha)
                      </Label>
                      <Field
                        as={Input}
                        id="timeUseBotQueues"
                        name="timeUseBotQueues"
                        type="number"
                        min="0"
                      />
                      <p className="text-xs text-muted-foreground">
                        0 = espera para sempre.
                      </p>
                    </div>

                    <Label htmlFor="maxUseBotQueues">
                      Chatbot desiste após quantas escolhas erradas
                    </Label>
                    <Field
                      as={Input}
                      id="maxUseBotQueues"
                      name="maxUseBotQueues"
                      type="number"
                      min="0"
                    />
                    <p className="text-xs text-muted-foreground">
                      Passando disso, o menu sai de cena e a conversa fica
                      aguardando alguém. 0 = insiste sempre.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-2">
                      <Workflow className="h-4 w-4 text-muted-foreground" />
                      Fluxo de automação
                    </Label>
                    <Select
                      value={selectedFlowId}
                      onValueChange={setSelectedFlowId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Nenhum fluxo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_FLOW}>Nenhum fluxo</SelectItem>
                        {activeFlows.map((flow) => (
                          <SelectItem key={flow.id} value={String(flow.id)}>
                            {flow.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      O fluxo é executado a cada mensagem recebida nesta conexão
                      (exceto grupos). Ele recebe nome, número, texto da
                      mensagem e o ticket como entrada.
                    </p>
                  </div>

                  {activeFlows.length === 0 && (
                    <div className="rounded-lg border border-dashed p-4 text-center">
                      <p className="text-sm text-muted-foreground">
                        Nenhum fluxo ativo disponível.
                      </p>
                      <RouterLink to="/flows" onClick={handleClose}>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-2"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Criar um fluxo
                        </Button>
                      </RouterLink>
                    </div>
                  )}
                </Secao>

                <Secao
                  titulo="Grupos e chamadas"
                  resumo={resumoGrupos(values)}
                  icone={Users}
                  aberta={secaoAberta === "grupos"}
                  onToggle={() => alternarSecao("grupos")}
                >
                  <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                    <div>
                      <Label
                        htmlFor="showGroups"
                        className="text-sm font-medium"
                      >
                        Atender grupos
                      </Label>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Exibe a aba Grupos na tela de atendimentos.
                      </p>
                    </div>
                    <Switch
                      id="showGroups"
                      checked={values.showGroups}
                      onCheckedChange={(checked) =>
                        setFieldValue("showGroups", checked)
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                    <div>
                      <Label
                        htmlFor="groupAsTicket"
                        className="text-sm font-medium"
                      >
                        Grupos viram atendimento
                      </Label>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Desligado, as mensagens de grupo são ignoradas — útil
                        para participar de grupos sem gerar fila.
                      </p>
                    </div>
                    <Switch
                      id="groupAsTicket"
                      checked={values.groupAsTicket}
                      onCheckedChange={(v) => setFieldValue("groupAsTicket", v)}
                    />
                  </div>

                  <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                    <div>
                      <Label
                        htmlFor="slaCountGroups"
                        className="text-sm font-medium"
                      >
                        Considerar grupos no SLA
                      </Label>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Quando desligado, grupos desta conexão não entram na contagem
                        de atendimentos em risco, fora do SLA, nem no tempo
                        médio de resposta. Recomendado: desligado para grupos
                        que não são atendimento ativo.
                      </p>
                    </div>
                    <Switch
                      id="slaCountGroups"
                      checked={Boolean(values.slaCountGroups)}
                      onCheckedChange={(v) =>
                        setFieldValue("slaCountGroups", v)
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                    <div>
                      <Label
                        htmlFor="readReceipts"
                        className="text-sm font-medium"
                      >
                        Confirmar visualização (visto azul)
                      </Label>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Ligado, quando o atendente abre ou marca como lido aqui no CRM,
                        o WhatsApp do cliente recebe o tique azul de visualizado.
                        Desligado, o atendimento fica como lido internamente,
                        mas o cliente não vê — útil para quem não quer avisar que leu.
                      </p>
                    </div>
                    <Switch
                      id="readReceipts"
                      checked={values.readReceipts !== false}
                      onCheckedChange={(v) => setFieldValue("readReceipts", v)}
                    />
                  </div>

                  <div className="space-y-3 rounded-lg border p-3">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <Label
                          htmlFor="importHistory"
                          className="text-sm font-medium"
                        >
                          Importar histórico do aparelho
                        </Label>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Traz as conversas que já estavam no celular e cria os
                          atendimentos. Vale na próxima conexão — desconecte e
                          leia o QR Code de novo: o histórico de todos os
                          contatos de uma vez só é enviado pelo WhatsApp no
                          momento em que o QR Code é lido. Depois de conectada,
                          use "Restaurar conversas" na lista de conexões, que
                          busca conversa por conversa. Nada é respondido
                          automaticamente: robô, fluxo, IA e saudação não rodam
                          sobre o que é importado.
                        </p>
                      </div>
                      <Switch
                        id="importHistory"
                        checked={Boolean(values.importHistory)}
                        onCheckedChange={(v) =>
                          setFieldValue("importHistory", v)
                        }
                      />
                    </div>

                    {values.importHistory && (
                      <div className="space-y-1.5">
                        <Label htmlFor="importTicketStatus">
                          Situação dos atendimentos importados
                        </Label>
                        <Select
                          value={values.importTicketStatus || "closed"}
                          onValueChange={(v) =>
                            setFieldValue("importTicketStatus", v)
                          }
                        >
                          <SelectTrigger id="importTicketStatus">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="closed">
                              Resolvido — fica no histórico
                            </SelectItem>
                            <SelectItem value="pending">
                              Aguardando — entra na fila da equipe
                            </SelectItem>
                            <SelectItem value="open">
                              Atendendo — abre como conversa em andamento
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Resolvido é o mais seguro: conversa antiga não passa
                          na frente de quem está esperando agora. Depois é só
                          reabrir a que interessar.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                    <div>
                      <Label
                        htmlFor="rejectCalls"
                        className="text-sm font-medium"
                      >
                        Recusar chamadas
                      </Label>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Ligações de voz e vídeo são recusadas automaticamente e
                        registradas no ticket.
                      </p>
                    </div>
                    <Switch
                      id="rejectCalls"
                      checked={values.rejectCalls}
                      onCheckedChange={(checked) =>
                        setFieldValue("rejectCalls", checked)
                      }
                    />
                  </div>

                  {values.rejectCalls && (
                    <div className="space-y-1.5">
                      <Label htmlFor="callRejectMessage">
                        Aviso ao recusar a chamada
                      </Label>
                      <Field
                        as={Textarea}
                        id="callRejectMessage"
                        name="callRejectMessage"
                        rows={3}
                        placeholder="Não atendemos por chamada. Envie sua dúvida por mensagem que já respondemos."
                      />
                      <p className="text-xs text-muted-foreground">
                        Deixe em branco para recusar sem enviar nada.
                      </p>
                    </div>
                  )}

                </Secao>

                <Secao
                  titulo="Integrações"
                  resumo={resumoIntegracoes(values)}
                  icone={Plug}
                  aberta={secaoAberta === "integracoes"}
                  onToggle={() => alternarSecao("integracoes")}
                >
                  <div className="space-y-1.5">
                    <Label htmlFor="token">Token da API</Label>
                    <Field
                      as={Input}
                      id="token"
                      name="token"
                      placeholder="Usado por sistemas externos para enviar por esta conexão"
                    />
                  </div>


                  <div className="space-y-1.5 rounded-lg border p-3">
                    <Label htmlFor="wavoipToken">Token da Wavoip</Label>
                    <Field
                      as={Input}
                      id="wavoipToken"
                      name="wavoipToken"
                      placeholder="Cole o token do painel da Wavoip"
                    />
                    <p className="text-xs text-muted-foreground">
                      Com o token, aparece o botão de ligar dentro do
                      atendimento. Em branco, ligações ficam desativadas nesta
                      conexão.
                    </p>
                  </div>
                </Secao>
              </div>
              )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  {i18n.t("whatsappModal.buttons.cancel")}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    modoCriacao && <QrCode className="h-4 w-4" />
                  )}
                  {modoCriacao
                    ? "Continuar para o QR Code"
                    : i18n.t("whatsappModal.buttons.okEdit")}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(WhatsAppModal);
